import React, { useRef, useEffect, useState, useCallback } from "react";
import { Volume2, VolumeX, RotateCcw } from "lucide-react";
import introVideo from "../assets/videos/RGB_LightningSATCHEL.mp4";

interface StartupVideoProps {
  onComplete: () => void;
}

/**
 * Full-screen startup video overlay for SATCHEL intro.
 * Displayed only in packaged Electron app on first startup (controlled by introSeen flag in app.jsonc).
 *
 * Features:
 * - Video is muted by default with mute/unmute toggle
 * - Replay button appears after video ends
 * - "Press any key to continue" prompt after video ends
 * - Tutorial panel slides in near video end with placeholder links
 * - Video stays on screen until user explicitly dismisses
 *
 * Can be dismissed by:
 * - Pressing any key (after video ends or during playback)
 * - Clicking anywhere on the overlay (except control buttons)
 */
const StartupVideo: React.FC<StartupVideoProps> = ({ onComplete }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [videoEnded, setVideoEnded] = useState(false);
  const [showTutorials, setShowTutorials] = useState(false);

  const handleComplete = useCallback(() => {
    // Mark as seen in app config (fire and forget; do not await)
    if (window.electronAPI) {
      window.electronAPI
        .updateAppConfig({
          introSeen: true,
          introSeenAt: new Date().toISOString(),
        })
        .catch(() => {
          /* ignore */
        });
    }
    onComplete();
  }, [onComplete]);

  // Handle video end - don't auto-dismiss, just show the prompt
  const handleVideoEnd = useCallback(() => {
    setVideoEnded(true);
    setShowTutorials(true);
  }, []);

  const handleVideoError = useCallback(() => {
    console.error("Error loading startup video");
    setHasError(true);
    // Skip video on error after a brief delay
    setTimeout(() => {
      handleComplete();
    }, 500);
  }, [handleComplete]);

  const handleVideoLoaded = useCallback(() => {
    setIsLoading(false);
  }, []);

  // Handle click to dismiss - works anywhere on the overlay
  // Control buttons use stopPropagation() to prevent this from firing
  const handleOverlayClick = useCallback(() => {
    handleComplete();
  }, [handleComplete]);

  // Toggle mute/unmute
  const handleToggleMute = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      setIsMuted((prev) => !prev);
      if (videoRef.current) {
        videoRef.current.muted = !isMuted;
      }
    },
    [isMuted],
  );

  // Replay video
  const handleReplay = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      setVideoEnded(false);
      setShowTutorials(false);
      videoRef.current.play().catch((error) => {
        console.error("Error replaying video:", error);
      });
    }
  }, []);

  // Handle keyboard to dismiss
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Dismiss on any key press
      event.preventDefault();
      handleComplete();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleComplete]);

  // Auto-play video when component mounts
  useEffect(() => {
    if (videoRef.current && !hasError) {
      videoRef.current.muted = true; // Ensure muted for autoplay
      videoRef.current.play().catch((error) => {
        console.error("Error auto-playing video:", error);
        // If autoplay fails (e.g., browser policy), show the ended state
        setVideoEnded(true);
        setShowTutorials(true);
      });
    }
  }, [hasError]);

  // Reveal tutorials when near the end even if ended event is missed
  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl) return;

    const handleTimeUpdate = () => {
      if (videoEl.duration && videoEl.currentTime >= videoEl.duration - 0.75) {
        setShowTutorials(true);
      }
    };

    videoEl.addEventListener("timeupdate", handleTimeUpdate);
    return () => {
      videoEl.removeEventListener("timeupdate", handleTimeUpdate);
    };
  }, []);

  // Tutorial/resource links - URLs TBD per acceptance criteria
  const tutorialLinks = [
    {
      title: "Getting Started with SATCHEL",
      url: "https://www.youtube.com/@smart-assets",
      duration: "TBD",
    },
    {
      title: "RGB Smart Assets Overview",
      url: "https://www.youtube.com/@smart-assets",
      duration: "TBD",
    },
    {
      title: "Lightning Network Integration",
      url: "https://www.youtube.com/@smart-assets",
      duration: "TBD",
    },
  ];

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black flex items-center justify-center cursor-pointer"
      onClick={handleOverlayClick}
      onKeyDown={() => {}} // Handled by window event listener
      role="button"
      tabIndex={0}
      aria-label="Click or press any key to continue"
      data-testid="startup-video-overlay"
    >
      {isLoading && !hasError && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-white text-lg">Loading...</div>
        </div>
      )}

      {hasError && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-sa-text-dim text-lg">Starting SATCHEL...</div>
        </div>
      )}

      <div className="flex items-center justify-center w-full h-full gap-6 px-6">
        <div
          className={`flex-1 flex items-center justify-center transition-all duration-500 ${showTutorials ? "max-w-[70%]" : "max-w-full"}`}
        >
          <video
            ref={videoRef}
            src={introVideo}
            className="w-full h-[80vh] object-contain rounded-lg bg-black"
            onEnded={handleVideoEnd}
            onError={handleVideoError}
            onLoadedData={handleVideoLoaded}
            playsInline
            muted={isMuted}
            data-testid="startup-video"
          >
            <track kind="captions" />
          </video>
        </div>

        {showTutorials && (
          // biome-ignore lint/a11y/noStaticElementInteractions: Container stops event propagation to prevent modal close
          <div
            className="w-[360px] bg-sa-surface/5 border border-white/15 rounded-lg p-4 text-white shadow-lg backdrop-blur-sm animate-in slide-in-from-right duration-500"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            data-testid="tutorial-panel"
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-lg font-semibold">Learn More</h3>
                <a
                  href="https://www.youtube.com/@smart-assets"
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary text-sm hover:underline"
                >
                  Visit Smart-Assets.io YouTube
                </a>
              </div>
            </div>
            <div className="space-y-3">
              {tutorialLinks.map((video) => (
                <a
                  key={video.title}
                  href={video.url}
                  target="_blank"
                  rel="noreferrer"
                  className="block bg-sa-surface/5 hover:bg-sa-surface/10 border border-white/10 rounded-md p-3 transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-sm">{video.title}</span>
                    <span className="text-xs text-sa-text">{video.duration}</span>
                  </div>
                  <div className="text-xs text-sa-text mt-1">YouTube - Smart-Assets.io</div>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Control buttons - lower right corner */}
      <div className="absolute bottom-8 right-8 flex items-center gap-3">
        {/* Replay button - only shown after video ends */}
        {videoEnded && (
          <button
            type="button"
            onClick={handleReplay}
            className="p-3 rounded-full bg-sa-surface/10 hover:bg-sa-surface/20 transition-colors border border-white/20"
            aria-label="Replay video"
            data-testid="replay-button"
          >
            <RotateCcw className="w-5 h-5 text-white" />
          </button>
        )}

        {/* Mute/Unmute button */}
        <button
          type="button"
          onClick={handleToggleMute}
          className="p-3 rounded-full bg-sa-surface/10 hover:bg-sa-surface/20 transition-colors border border-white/20"
          aria-label={isMuted ? "Unmute video" : "Mute video"}
          data-testid="mute-button"
        >
          {isMuted ? (
            <VolumeX className="w-5 h-5 text-white" />
          ) : (
            <Volume2 className="w-5 h-5 text-white" />
          )}
        </button>
      </div>

      {/* Skip/Continue hint - changes based on video state */}
      <div
        className={`absolute bottom-8 left-1/2 transform -translate-x-1/2 text-sm transition-all duration-300 ${
          videoEnded
            ? "text-white font-medium text-base animate-pulse"
            : "text-sa-text-dim opacity-70"
        }`}
        data-testid="hint-text"
      >
        {videoEnded ? "Click or press any key to continue" : "Click or press any key to skip"}
      </div>
    </div>
  );
};

export default StartupVideo;
